import {
  PAID_INVOICE_STATUS,
  PAST_DUE_INVOICE_STATUS,
  PENDING_INVOICE_STATUS,
  PROCESSING_INVOICE_STATUS,
  UNPAID_AND_OVERDUE_INVOICE_STATUS,
  UNPAID_INVOICE_STATUS,
} from "@/constants/invoice";

const updateInvoicePermission = (_user, invoice) =>
  ![
    PAID_INVOICE_STATUS,
    PENDING_INVOICE_STATUS,
    PROCESSING_INVOICE_STATUS,
  ].includes(invoice?.invoice_info?.invoice_status?.toLowerCase() || "");

const deleteInvoicePermission = (_user, invoice) =>
  ![
    PAID_INVOICE_STATUS,
    PENDING_INVOICE_STATUS,
    PROCESSING_INVOICE_STATUS,
  ].includes(invoice?.invoice_info.invoice_status);

const createMRDCPermission = (user, data) =>
  data === "flag"
    ? user?.client_tenancy_info?.feature_flags?.mrdc
    : user?.client_tenancy_info?.feature_flags?.mrdc &&
      [
        UNPAID_INVOICE_STATUS,
        UNPAID_AND_OVERDUE_INVOICE_STATUS,
        PAST_DUE_INVOICE_STATUS,
      ].includes(data?.invoice_info?.invoice_status);

const OWNER_PERMISSIONS = {
  clientRoutes: {
    view: true,
  },
  customerRoutes: {
    view: false,
  },
  guestRoutes: {
    view: false,
  },
  contact: {
    view: true,
    create: true,
    update: true,
    delete: true,
  },
  invoice: {
    view: true,
    create: true,
    update: updateInvoicePermission,
    delete: deleteInvoicePermission,
  },
  payment_method: {
    view: true,
    create: true,
    update: true,
    delete: true,
  },
  transaction: {
    view: true,
  },
  settings: {
    view: true,
    update: true,
  },
  mrdc: {
    view: true,
    create: createMRDCPermission,
    update: true,
    delete: true,
  },
};

const AUTH_USER_PERMISSIONS = {
  clientRoutes: {
    view: true,
  },
  customerRoutes: {
    view: false,
  },
  guestRoutes: {
    view: false,
  },
  contact: {
    view: true,
    create: true,
    update: true,
    delete: true,
  },
  invoice: {
    view: true,
    create: true,
    update: updateInvoicePermission,
    delete: deleteInvoicePermission,
  },
  payment_method: {
    view: true,
    create: true,
    update: true,
    delete: true,
  },
  transaction: {
    view: true,
  },
  settings: {
    view: true,
    update: true,
  },
  mrdc: {
    view: true,
    create: createMRDCPermission,
    update: true,
    delete: true,
  },
};

const ADMIN_PERMISSIONS = {
  clientRoutes: {
    view: true,
  },
  customerRoutes: {
    view: false,
  },
  guestRoutes: {
    view: false,
  },
  contact: {
    view: true,
    create: true,
    update: true,
    delete: true,
  },
  invoice: {
    view: true,
    create: true,
    update: updateInvoicePermission,
    delete: deleteInvoicePermission,
  },
  payment_method: {
    view: false,
    create: false,
    update: false,
    delete: false,
  },
  transaction: {
    view: true,
  },
  settings: {
    view: true,
    update: true,
  },
  mrdc: {
    view: false,
    create: createMRDCPermission,
    update: false,
    delete: false,
  },
};

const USER_PERMISSIONS = {
  clientRoutes: {
    view: false,
  },
  customerRoutes: {
    view: true,
  },
  guestRoutes: {
    view: false,
  },
  contact: {},
  invoice: {
    view: true,
  },
  payment_method: {
    view: true,
    create: true,
    update: true,
    delete: true,
  },
  transaction: {},
  settings: {
    view: true,
  },
  mrdc: {},
};

const GUEST_PERMISSIONS = {
  clientRoutes: {
    view: false,
  },
  customerRoutes: {
    view: false,
  },
  guestRoutes: {
    view: true,
  },
  contact: {},
  invoice: {},
  payment_method: {},
  transaction: {},
  settings: {},
  mrdc: {},
};

const ROLES = {
  owner: OWNER_PERMISSIONS,
  auth_user: AUTH_USER_PERMISSIONS,
  admin: ADMIN_PERMISSIONS,
  user: USER_PERMISSIONS,
  guest: GUEST_PERMISSIONS,
};

export function hasPermission(user, resource, action, data) {
  const role = user?.cognito_user_info?.UserRole || "guest";
  const permission = ROLES[role]?.[resource]?.[action];
  if (permission === null || permission === undefined) return false;

  if (typeof permission === "boolean") return permission;
  return data !== null && data !== undefined && permission(user, data);
}
